import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { jobAPI, clientSettingsAPI } from '../../../services';
import { useAuth } from '../../../context/AuthContext';

const JobFormModal = ({ job, onClose, onSuccess }) => {
  const { user } = useAuth();
  const isEditMode = !!job;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  
  // Check if user is admin/hr_head who can create jobs for any department
  const userRole = user?.role || user?.employee?.role;
  const canCreateForAnyDept = ['admin', 'superadmin', 'hr_head'].includes(userRole);
  
  const [form, setForm] = useState({
    title: '',
    department: '',
    designation: '',
    description: '',
    experience: { min: 0, max: 0 },
    skills: [],
    qualifications: [],
    positions: 1,
    employmentType: 'full_time',
    location: '',
    salaryRange: { min: '', max: '', currency: 'INR' },
    tatDays: 30,
    priority: 'medium',
    requestNotes: ''
  });

  const [skillInput, setSkillInput] = useState('');
  const [qualificationInput, setQualificationInput] = useState('');

  useEffect(() => {
    fetchDepartmentsAndDesignations();
  }, []);

  useEffect(() => {
    if (job) {
      setForm({
        title: job.title || '',
        department: job.department || '',
        designation: job.designation || '',
        description: job.description || '',
        experience: job.experience || { min: 0, max: 0 },
        skills: job.skills || [],
        qualifications: job.qualifications || [],
        positions: job.positions || 1,
        employmentType: job.employmentType || 'full_time',
        location: job.location || '',
        salaryRange: job.salaryRange || { min: '', max: '', currency: 'INR' },
        tatDays: job.tatDays || 30,
        priority: job.priority || 'medium',
        requestNotes: job.requestNotes || ''
      });
    }
  }, [job]);

  const fetchDepartmentsAndDesignations = async () => {
    try {
      const [deptRes, desigRes] = await Promise.all([
        clientSettingsAPI.getDepartments(),
        clientSettingsAPI.getDesignations()
      ]);
      
      if (deptRes.status === 'success') {
        // Admin/HR Head can create jobs for any active department
        // HOD can only create jobs for departments with canRaiseJobRequest enabled
        const activeDepts = (deptRes.data || []).filter(d => {
          if (d.isActive === false) return false;
          return canCreateForAnyDept || d.canRaiseJobRequest;
        });
        setDepartments(activeDepts);
      }
      
      if (desigRes.status === 'success') {
        setDesignations(desigRes.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch departments:', err);
    }
  };

  // Filter designations based on selected department
  const filteredDesignations = form.department
    ? designations.filter(d => {
        const dept = departments.find(dp => dp.name === form.department);
        return dept && String(d.department) === String(dept._id);
      })
    : designations;

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    // Reset designation when department changes
    if (field === 'department') {
      setForm(prev => ({ ...prev, [field]: value, designation: '' }));
    }
  };

  const handleNestedChange = (parent, field, value) => {
    setForm(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const addSkill = () => {
    if (skillInput.trim() && !form.skills.includes(skillInput.trim())) {
      setForm(prev => ({ ...prev, skills: [...prev.skills, skillInput.trim()] }));
      setSkillInput('');
    }
  };

  const removeSkill = (index) => {
    setForm(prev => ({
      ...prev,
      skills: prev.skills.filter((_, i) => i !== index)
    }));
  };

  const addQualification = () => {
    if (qualificationInput.trim() && !form.qualifications.includes(qualificationInput.trim())) {
      setForm(prev => ({ ...prev, qualifications: [...prev.qualifications, qualificationInput.trim()] }));
      setQualificationInput('');
    }
  };

  const removeQualification = (index) => {
    setForm(prev => ({
      ...prev,
      qualifications: prev.qualifications.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!form.title.trim()) {
      setError('Job title is required');
      return;
    }
    if (!form.department.trim()) {
      setError('Department is required');
      return;
    }
    if (!form.designation.trim()) {
      setError('Designation is required');
      return;
    }
    if (!form.description.trim()) {
      setError('Job description is required');
      return;
    }
    
    try {
      setLoading(true);
      
      const payload = {
        ...form,
        salaryRange: {
          min: form.salaryRange.min ? parseInt(form.salaryRange.min) : undefined,
          max: form.salaryRange.max ? parseInt(form.salaryRange.max) : undefined,
          currency: form.salaryRange.currency
        }
      };
      
      if (isEditMode) {
        await jobAPI.updateJob(job._id, payload);
      } else {
        await jobAPI.createJob(payload);
      }
      
      onSuccess();
    } catch (err) {
      setError(err.message || 'Failed to save job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-gray-900">
            {isEditMode ? 'Edit Job Request' : 'Create Job Request'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
              {error}
            </div>
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Senior Software Engineer"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Department <span className="text-red-500">*</span>
              </label>
              <select
                value={form.department}
                onChange={(e) => handleChange('department', e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Department</option>
                {departments.map((dept) => (
                  <option key={dept._id} value={dept.name}>
                    {dept.name}
                  </option>
                ))}
              </select>
              {departments.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  No departments enabled for job requests
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Designation <span className="text-red-500">*</span>
              </label>
              <select
                value={form.designation}
                onChange={(e) => handleChange('designation', e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                disabled={!form.department}
              >
                <option value="">Select Designation</option>
                {filteredDesignations.map((desig) => (
                  <option key={desig._id} value={desig.name}>
                    {desig.name}
                  </option>
                ))}
              </select>
              {form.department && filteredDesignations.length === 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  No designations found for this department
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => handleChange('location', e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Hyderabad"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Describe the role, responsibilities, and expectations..."
            />
          </div>

          {/* Experience & Positions */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Experience (Years)
              </label>
              <input
                type="number"
                min="0"
                value={form.experience.min}
                onChange={(e) => handleNestedChange('experience', 'min', parseInt(e.target.value) || 0)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Experience (Years)
              </label>
              <input
                type="number"
                min="0"
                value={form.experience.max}
                onChange={(e) => handleNestedChange('experience', 'max', parseInt(e.target.value) || 0)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Positions
              </label>
              <input
                type="number"
                min="1"
                value={form.positions}
                onChange={(e) => handleChange('positions', parseInt(e.target.value) || 1)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Employment Type
              </label>
              <select
                value={form.employmentType}
                onChange={(e) => handleChange('employmentType', e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="full_time">Full Time</option>
                <option value="part_time">Part Time</option>
                <option value="contract">Contract</option>
                <option value="internship">Internship</option>
              </select>
            </div>
          </div>

          {/* Salary Range */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Salary (LPA)
              </label>
              <input
                type="number"
                min="0"
                value={form.salaryRange.min}
                onChange={(e) => handleNestedChange('salaryRange', 'min', e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 800000"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Salary (LPA)
              </label>
              <input
                type="number"
                min="0"
                value={form.salaryRange.max}
                onChange={(e) => handleNestedChange('salaryRange', 'max', e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 1200000"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Currency
              </label>
              <select
                value={form.salaryRange.currency}
                onChange={(e) => handleNestedChange('salaryRange', 'currency', e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>

          {/* Skills */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Required Skills
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Type a skill and press Enter"
              />
              <button
                type="button"
                onClick={addSkill}
                className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.skills.map((skill, index) => (
                <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                  {skill}
                  <button type="button" onClick={() => removeSkill(index)} className="hover:text-red-600">
                    <X className="w-4 h-4" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Qualifications */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Qualifications
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={qualificationInput}
                onChange={(e) => setQualificationInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addQualification())}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., B.Tech in Computer Science"
              />
              <button
                type="button"
                onClick={addQualification}
                className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.qualifications.map((qual, index) => (
                <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm">
                  {qual}
                  <button type="button" onClick={() => removeQualification(index)} className="hover:text-red-600">
                    <X className="w-4 h-4" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* TAT & Priority */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                TAT (Days) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                value={form.tatDays}
                onChange={(e) => handleChange('tatDays', parseInt(e.target.value) || 30)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Deadline will be set to this many days after HR approval
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={form.priority}
                onChange={(e) => handleChange('priority', e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          {/* Request Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Additional Notes
            </label>
            <textarea
              value={form.requestNotes}
              onChange={(e) => handleChange('requestNotes', e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Any additional notes for HR..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : isEditMode ? 'Update Job' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobFormModal;
